import { useEffect, useState } from "react";
import { btn } from "@/components/ds/styles";
import { Link } from "@tanstack/react-router";
import {
  CONSENT_STORAGE_KEY,
  OPEN_CONSENT_EVENT,
  applyConsent,
  detectCountry,
  needsBanner,
  readConsent,
  saveConsent,
  type ConsentChoice,
} from "@/lib/consent";

/** Shown only in consent regions without a stored choice, or when reopened from the footer. */
export function ConsentBanner() {
  const [open, setOpen] = useState(false);
  const [region, setRegion] = useState<string | null>(null);
  const [current, setCurrent] = useState<ConsentChoice | null>(null);

  useEffect(() => {
    let alive = true;
    const stored = readConsent();
    setCurrent(stored?.choice ?? null);
    detectCountry().then((c) => {
      if (!alive) return;
      setRegion(c);
      if (!stored && needsBanner(c)) setOpen(true);
    });
    const reopen = () => {
      setCurrent(readConsent()?.choice ?? null);
      setOpen(true);
    };
    const sync = (e: StorageEvent) => {
      if (e.key !== CONSENT_STORAGE_KEY) return;
      const r = readConsent();
      if (r) applyConsent(r.choice);
      setCurrent(r?.choice ?? null);
    };
    window.addEventListener(OPEN_CONSENT_EVENT, reopen);
    window.addEventListener("storage", sync);
    return () => {
      alive = false;
      window.removeEventListener(OPEN_CONSENT_EVENT, reopen);
      window.removeEventListener("storage", sync);
    };
  }, []);

  if (!open) return null;

  const choose = (c: ConsentChoice) => {
    saveConsent(c, region);
    setCurrent(c);
    setOpen(false);
  };

  return (
    <div role="dialog" aria-labelledby="consent-title" className="fixed inset-x-0 bottom-0 z-50 rounded-t-ds-lg border-0 bg-surface md:border md:border-hairline-default p-5 pb-[max(20px,env(safe-area-inset-bottom))] shadow-lg animate-in slide-in-from-bottom-4 fade-in-0 duration-300 motion-reduce:animate-none md:inset-x-auto md:bottom-4 md:end-4 md:w-[26rem] md:rounded-ds-lg md:pb-5">
      <span aria-hidden className="mx-auto mb-4 block h-[5px] w-11 rounded-ds-pill bg-cream-300 md:hidden" />
      <p id="consent-title" className="font-display text-[17px] font-bold text-brown-700">إعدادات الخصوصية</p>
      <p className="mt-2 text-[15px] leading-[1.8] text-brown-500">
        منستخدم Google Analytics لنفهم كيف تُستخدم الصفحات. الموافقة اختيارية، وفيك تغيّرها بأي وقت.{" "}
        <Link to="/privacy" className="inline-flex min-h-11 items-center font-bold text-amber-800 underline underline-offset-4">التفاصيل</Link>
      </p>
      {current && (
        <p className="mt-2 text-[13px] text-brown-500">
          اختيارك الحالي: {current === "granted" ? "موافقة" : "رفض"}
        </p>
      )}
      <div className="mt-4 flex gap-3">
        <button type="button" onClick={() => choose("granted")} className={`${btn.primary} flex-1`}>
          موافقة
        </button>
        <button type="button" onClick={() => choose("denied")} className="min-h-12 flex-1 rounded-ds-pill border border-hairline-default bg-surface px-5 font-display text-[16px] font-bold text-brown-700 transition-colors duration-ds-fast hover:bg-cream-50">
          رفض
        </button>
      </div>
    </div>
  );
}
